// src/shared/categoryCombo.js — изборът на категория на разхода (ПРОМТ-46).
//
// Макс (02.11.2026): вместо дълго падащо меню — търсачка: пишеш „нае" и излиза
// „Наем"; и да се вижда групата (Материали 601 / Услуги 602 / Други 609).
// ЕДИН компонент за всички места, където се избира категория — редът на
// доставката, филтърът в списъка, категорията по подразбиране в „Настройки за
// Конто" — за да не се разминават три менюта с различни правила.
//
// Търси се по името и по синонимите/ключовите думи (ПРОМТ-45): „ток" →
// Ел. енергия, „виваком" → Телефон и интернет, „бензин" → Гориво. В името —
// навсякъде („ма" намира и „Реклама"); в синонимите — само от началото на
// дума, иначе „нае" би намерило всяка статия с „наем" някъде в синонимите.

import { combobox } from './combobox.js';
import { GROUPS, groupOf, groupTitle } from '../../services/categoryGroups.js';

const lower = (s) => String(s == null ? '' : s).toLowerCase();
const wordsOf = (s) => lower(s).split(/[^0-9a-zа-я]+/i).filter(Boolean);

function groupKey(c) { return c.group || groupOf(c.account) || 'other'; }

// Колко добре съвпада категорията: 0 — името започва с търсеното, 1 — дума от
// името, 2 — някъде в името, 3 — синоним/ключова дума; null — не съвпада.
export function categoryScore(c, words) {
  const name = lower(c.name);
  const nameWords = wordsOf(c.name);
  const extra = wordsOf([].concat(c.synonyms || [], c.keywords || []).join(' '));
  let worst = 0;
  for (const w of words) {
    let s;
    if (name.startsWith(w)) s = 0;
    else if (nameWords.some((x) => x.startsWith(w))) s = 1;
    else if (name.includes(w)) s = 2;
    else if (extra.some((x) => x.startsWith(w))) s = 3;
    else return null;
    worst = Math.max(worst, s);
  }
  return worst;
}

// Секциите за списъка: при празно поле първо предложените (паметта по ЕИК,
// после разчетената от OCR), после всички по групи; при писане — групата с
// повече съвпадения е отгоре, вътре по-доброто съвпадение е първо.
export function categorySections(found, q, suggestedIds) {
  const words = lower(q).trim().split(/\s+/).filter(Boolean);
  const top = [];
  if (!words.length) {
    for (const id of (suggestedIds || [])) {
      const c = found.find((x) => x.id === id);
      if (c && !top.includes(c)) top.push(c);
    }
  }
  const rest = found.filter((c) => !top.includes(c));
  const order = (c) => (words.length ? categoryScore(c, words) : 0);
  const secs = GROUPS.map((g, gi) => {
    const items = rest.map((c, i) => ({ c, i, s: order(c) })).filter((x) => groupKey(x.c) === g.key)
      .sort((a, b) => a.s - b.s || a.i - b.i).map((x) => x.c);
    return { key: g.key, gi, items, label: groupTitle(g.key, items.map((c) => c.account)) };
  }).filter((s) => s.items.length);
  if (words.length) secs.sort((a, b) => b.items.length - a.items.length || a.gi - b.gi);
  return (top.length ? [{ key: 'suggested', label: 'Предложени', items: top }] : []).concat(secs);
}

// opts:
//   categories  — общите + своите на фирмата (от /purchases/categories)
//   value       — ключът на избраната или ''
//   suggested() — ключовете на предложените (най-отгоре при празно поле)
//   onPick(cat) — избрана категория или null при изчистване
//   ariaLabel, placeholder, disabled, className, inline (за диалог)
export function categoryCombo(opts) {
  const o = opts || {};
  const all = Array.isArray(o.categories) ? o.categories : [];
  // архивираната своя категория — само на реда, който вече я има
  const items = all.filter((c) => !c.archived_at || c.id === o.value);
  return combobox({
    items,
    label: (c) => c.name || '',
    sub: (c) => (c.archived_at ? 'архивирана' : c.own ? 'на фирмата' : ''),
    side: (c) => String(c.account || ''),
    match: (c, words) => categoryScore(c, words) !== null,
    sections: (found, q) => categorySections(found, q, typeof o.suggested === 'function' ? o.suggested() : o.suggested),
    value: o.value || null,
    limit: 200,
    // „нае" + Tab: едно съвпадение — то е изборът (не се губи написаното)
    autoPickSingle: true,
    mobileFull: true,
    // в диалог (Настройки за Конто) списъкът е в потока — иначе се отрязва
    inline: !!o.inline,
    // в таблицата на редовете (с плъзгач) списъкът плава над нея — иначе се реже
    float: !o.inline,
    className: 'combo-cat' + (o.className ? ' ' + o.className : ''),
    placeholder: o.placeholder || 'Пишете — напр. „наем"',
    ariaLabel: o.ariaLabel || null,
    emptyText: 'Няма такава категория — опитайте с друга дума.',
    disabled: !!o.disabled,
    onPick: (c) => { if (o.onPick) o.onPick(c || null); },
  });
}
